/**
 * Event-browser helpers — faceting + filtering for the Chronology event browser.
 *
 * Pure functions over the bounded event list returned by
 * `GET /chronologies/{id}/event-browser`. The browser pulls the full (capped)
 * set once and facets/filters client-side — responsive, and honest about the
 * post-decrypt bounded-page limit (the backend can't SQL-filter encrypted text).
 * Participant names arrive already grouped by the backend's read-time
 * normalization, so counts here reflect real people, not fragments.
 */

import { participantName } from './chronologyEntry';

/** Distinct participant display names on an event. */
export function eventParticipantNames(ev) {
  return (ev.participants || []).map(participantName).filter(Boolean);
}

/** Facet buckets (value + count) for event_type and participant, sorted by
 *  count desc. Computed over the full event list so counts are stable. */
export function computeFacets(events) {
  const types = new Map();
  const participants = new Map();
  for (const ev of events || []) {
    if (ev.event_type) types.set(ev.event_type, (types.get(ev.event_type) || 0) + 1);
    for (const name of eventParticipantNames(ev)) {
      participants.set(name, (participants.get(name) || 0) + 1);
    }
  }
  const toSorted = (m) =>
    [...m.entries()].map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count || String(a.value).localeCompare(String(b.value)));
  return { types: toSorted(types), participants: toSorted(participants) };
}

/** Does one event pass the active filter set? */
export function eventMatches(ev, f) {
  if (f.onlyMissing && ev.already_in_chronology) return false;
  if (f.types?.size && !f.types.has(ev.event_type)) return false;
  if (f.participants?.size) {
    const names = eventParticipantNames(ev);
    if (!names.some((n) => f.participants.has(n))) return false;
  }
  if (f.dateFrom || f.dateTo) {
    const d = ev.occurred_at ? String(ev.occurred_at).slice(0, 10) : '';
    if (!d) return false; // undated events drop out when a date range is set
    if (f.dateFrom && d < f.dateFrom) return false;
    if (f.dateTo && d > f.dateTo) return false;
  }
  if (f.query) {
    const q = f.query.toLowerCase();
    const hay = `${ev.description || ''} ${eventParticipantNames(ev).join(' ')} ${ev.event_type || ''}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
}

export function filterEvents(events, f) {
  return (events || []).filter((ev) => eventMatches(ev, f));
}
